// Local Storage utilities for collections, favorites, and user data
const STORAGE_KEYS = {
  FAVORITES: 'moviehub-favorites',
  COLLECTIONS: 'moviehub-collections',
  WATCHED: 'moviehub-watched',
  WATCHLIST: 'moviehub-watchlist',
  USER_RATINGS: 'moviehub-user-ratings',
  THEME: 'moviehub-theme',
  PREFERENCES: 'moviehub-preferences'
}

// Utility to safely parse JSON from localStorage
const safeJSONParse = (value, defaultValue = []) => {
  try {
    return value ? JSON.parse(value) : defaultValue
  } catch (error) {
    console.error('Error parsing JSON from localStorage:', error)
    return defaultValue
  }
}

// Utility to safely stringify and store data
const safeJSONStore = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data))
    return true
  } catch (error) {
    console.error('Error storing data to localStorage:', error)
    return false
  }
}

// ============ FAVORITES ============

export const getFavorites = () => {
  const favorites = localStorage.getItem(STORAGE_KEYS.FAVORITES)
  return safeJSONParse(favorites, [])
}

export const addToFavorites = (movie) => {
  const favorites = getFavorites()
  const isAlreadyFavorite = favorites.some(fav => fav.id === movie.id)
  
  if (!isAlreadyFavorite) {
    const movieData = {
      id: movie.id,
      title: movie.title,
      poster_path: movie.poster_path,
      vote_average: movie.vote_average,
      release_date: movie.release_date,
      overview: movie.overview,
      genre_ids: movie.genre_ids || [],
      dateAdded: new Date().toISOString()
    }
    
    favorites.push(movieData)
    return safeJSONStore(STORAGE_KEYS.FAVORITES, favorites)
  }
  
  return false
}

export const removeFromFavorites = (movieId) => {
  const favorites = getFavorites()
  const updatedFavorites = favorites.filter(fav => fav.id !== movieId)
  return safeJSONStore(STORAGE_KEYS.FAVORITES, updatedFavorites)
}

export const isFavorite = (movieId) => {
  const favorites = getFavorites()
  return favorites.some(fav => fav.id === movieId)
}

// ============ COLLECTIONS ============

export const getCollections = () => {
  const collections = localStorage.getItem(STORAGE_KEYS.COLLECTIONS)
  return safeJSONParse(collections, [])
}

export const createCollection = (name, description = '') => {
  const collections = getCollections()
  const existingCollection = collections.find(col => col.name.toLowerCase() === name.toLowerCase())
  
  if (existingCollection) {
    throw new Error('Collection with this name already exists')
  }
  
  const newCollection = {
    id: Date.now().toString(),
    name: name.trim(),
    description: description.trim(),
    movies: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
  
  collections.push(newCollection)
  safeJSONStore(STORAGE_KEYS.COLLECTIONS, collections)
  
  return newCollection
}

export const deleteCollection = (collectionId) => {
  const collections = getCollections()
  const updatedCollections = collections.filter(col => col.id !== collectionId)
  return safeJSONStore(STORAGE_KEYS.COLLECTIONS, updatedCollections)
}

export const updateCollection = (collectionId, updates) => {
  const collections = getCollections()
  const collectionIndex = collections.findIndex(col => col.id === collectionId)
  
  if (collectionIndex !== -1) {
    collections[collectionIndex] = {
      ...collections[collectionIndex],
      ...updates,
      updatedAt: new Date().toISOString()
    }
    
    safeJSONStore(STORAGE_KEYS.COLLECTIONS, collections)
    return collections[collectionIndex]
  }
  
  return null
}

export const addMovieToCollection = (collectionId, movie) => {
  const collections = getCollections()
  const collection = collections.find(col => col.id === collectionId)
  
  if (!collection) return false
  
  const isAlreadyInCollection = collection.movies.some(m => m.id === movie.id)
  if (isAlreadyInCollection) return false
  
  const movieData = {
    id: movie.id,
    title: movie.title,
    poster_path: movie.poster_path,
    vote_average: movie.vote_average,
    release_date: movie.release_date,
    overview: movie.overview,
    genre_ids: movie.genre_ids || [],
    dateAdded: new Date().toISOString()
  }
  
  collection.movies.push(movieData)
  collection.updatedAt = new Date().toISOString()
  
  return safeJSONStore(STORAGE_KEYS.COLLECTIONS, collections)
}

export const removeMovieFromCollection = (collectionId, movieId) => {
  const collections = getCollections()
  const collection = collections.find(col => col.id === collectionId)
  
  if (!collection) return false
  
  collection.movies = collection.movies.filter(movie => movie.id !== movieId)
  collection.updatedAt = new Date().toISOString()
  
  return safeJSONStore(STORAGE_KEYS.COLLECTIONS, collections)
}

export const getCollectionById = (collectionId) => {
  const collections = getCollections()
  return collections.find(col => col.id === collectionId) || null
}

export const isMovieInCollection = (collectionId, movieId) => {
  const collection = getCollectionById(collectionId)
  return collection ? collection.movies.some(movie => movie.id === movieId) : false
}

// ============ WATCHLIST ============

export const getWatchlist = () => {
  const watchlist = localStorage.getItem(STORAGE_KEYS.WATCHLIST)
  return safeJSONParse(watchlist, [])
}

export const addToWatchlist = (movie) => {
  const watchlist = getWatchlist()
  const isAlreadyInWatchlist = watchlist.some(item => item.id === movie.id)
  
  if (!isAlreadyInWatchlist) {
    const movieData = {
      id: movie.id,
      title: movie.title,
      poster_path: movie.poster_path,
      vote_average: movie.vote_average,
      release_date: movie.release_date,
      overview: movie.overview,
      genre_ids: movie.genre_ids || [],
      dateAdded: new Date().toISOString()
    }
    
    watchlist.push(movieData)
    return safeJSONStore(STORAGE_KEYS.WATCHLIST, watchlist)
  }
  
  return false
}

export const removeFromWatchlist = (movieId) => {
  const watchlist = getWatchlist()
  const updatedWatchlist = watchlist.filter(item => item.id !== movieId)
  return safeJSONStore(STORAGE_KEYS.WATCHLIST, updatedWatchlist)
}

export const isInWatchlist = (movieId) => {
  const watchlist = getWatchlist()
  return watchlist.some(item => item.id === movieId)
}

// ============ WATCHED MOVIES ============

export const getWatchedMovies = () => {
  const watched = localStorage.getItem(STORAGE_KEYS.WATCHED)
  return safeJSONParse(watched, [])
}

export const markAsWatched = (movie, userRating = null) => {
  const watched = getWatchedMovies()
  const existingIndex = watched.findIndex(item => item.id === movie.id)
  
  const movieData = {
    id: movie.id,
    title: movie.title,
    poster_path: movie.poster_path,
    vote_average: movie.vote_average,
    release_date: movie.release_date,
    overview: movie.overview,
    genre_ids: movie.genre_ids || [],
    userRating: userRating,
    watchedDate: new Date().toISOString()
  }
  
  if (existingIndex !== -1) {
    watched[existingIndex] = movieData
  } else {
    watched.push(movieData)
    // Remove from watchlist if it exists there
    removeFromWatchlist(movie.id)
  }
  
  return safeJSONStore(STORAGE_KEYS.WATCHED, watched)
}

export const removeFromWatched = (movieId) => {
  const watched = getWatchedMovies()
  const updatedWatched = watched.filter(item => item.id !== movieId)
  return safeJSONStore(STORAGE_KEYS.WATCHED, updatedWatched)
}

export const isWatched = (movieId) => {
  const watched = getWatchedMovies()
  return watched.some(item => item.id === movieId)
}

// ============ USER RATINGS ============

export const getUserRatings = () => {
  const ratings = localStorage.getItem(STORAGE_KEYS.USER_RATINGS)
  return safeJSONParse(ratings, {})
}

export const setUserRating = (movieId, rating) => {
  const ratings = getUserRatings()
  ratings[movieId] = {
    rating: rating,
    dateRated: new Date().toISOString()
  }
  
  return safeJSONStore(STORAGE_KEYS.USER_RATINGS, ratings)
}

export const getUserRating = (movieId) => {
  const ratings = getUserRatings()
  return ratings[movieId]?.rating || null
}

export const removeUserRating = (movieId) => {
  const ratings = getUserRatings()
  delete ratings[movieId]
  return safeJSONStore(STORAGE_KEYS.USER_RATINGS, ratings)
}

// ============ THEME MANAGEMENT ============

export const getTheme = () => {
  return localStorage.getItem(STORAGE_KEYS.THEME) || 'dark'
}

export const setTheme = (theme) => {
  localStorage.setItem(STORAGE_KEYS.THEME, theme)
  
  // Apply theme to document
  if (theme === 'dark') {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
  
  return true
}

// ============ USER PREFERENCES ============

export const getPreferences = () => {
  const prefs = localStorage.getItem(STORAGE_KEYS.PREFERENCES)
  return safeJSONParse(prefs, {
    autoplay: false,
    showAdultContent: false,
    defaultView: 'grid',
    moviesPerPage: 20
  })
}

export const updatePreferences = (newPrefs) => {
  const currentPrefs = getPreferences()
  const updatedPrefs = { ...currentPrefs, ...newPrefs }
  return safeJSONStore(STORAGE_KEYS.PREFERENCES, updatedPrefs)
}

// ============ STATISTICS ============

export const getStatistics = () => {
  const favorites = getFavorites()
  const watched = getWatchedMovies()
  const watchlist = getWatchlist()
  const collections = getCollections()
  const ratings = getUserRatings()
  
  const userRatingValues = Object.values(ratings).map(r => r.rating)
  const averageUserRating = userRatingValues.length > 0 
    ? userRatingValues.reduce((sum, rating) => sum + rating, 0) / userRatingValues.length 
    : 0
  
  // Genre statistics
  const genreCount = {}
  favorites.forEach(movie => {
    movie.genre_ids?.forEach(genreId => {
      genreCount[genreId] = (genreCount[genreId] || 0) + 1
    })
  })
  
  const topGenre = Object.keys(genreCount).reduce((a, b) => 
    genreCount[a] > genreCount[b] ? a : b, null
  )
  
  return {
    totalFavorites: favorites.length,
    totalWatched: watched.length,
    totalWatchlist: watchlist.length,
    totalCollections: collections.length,
    totalRated: userRatingValues.length,
    averageUserRating: Math.round(averageUserRating * 10) / 10,
    topGenre: topGenre,
    totalMoviesInCollections: collections.reduce((sum, col) => sum + col.movies.length, 0)
  }
}

// ============ SEARCH HISTORY ============

const SEARCH_HISTORY_KEY = 'moviehub-search-history'

export const getSearchHistory = () => {
  const history = localStorage.getItem(SEARCH_HISTORY_KEY)
  return safeJSONParse(history, [])
}

export const addToSearchHistory = (query) => {
  if (!query.trim()) return false
  
  const history = getSearchHistory()
  const normalizedQuery = query.trim().toLowerCase()
  
  // Remove if already exists
  const filteredHistory = history.filter(item => item.query.toLowerCase() !== normalizedQuery)
  
  // Add to beginning
  filteredHistory.unshift({
    query: query.trim(),
    date: new Date().toISOString()
  })
  
  // Keep only last 10 searches
  const limitedHistory = filteredHistory.slice(0, 10)
  
  return safeJSONStore(SEARCH_HISTORY_KEY, limitedHistory)
}

export const clearSearchHistory = () => {
  localStorage.removeItem(SEARCH_HISTORY_KEY)
  return true
}

// ============ EXPORT/IMPORT DATA ============

export const exportUserData = () => {
  const data = {
    favorites: getFavorites(),
    collections: getCollections(),
    watched: getWatchedMovies(),
    watchlist: getWatchlist(),
    ratings: getUserRatings(),
    preferences: getPreferences(),
    searchHistory: getSearchHistory(),
    exportDate: new Date().toISOString(),
    version: '1.0'
  }
  
  return JSON.stringify(data, null, 2)
}

export const importUserData = (jsonData) => {
  try {
    const data = JSON.parse(jsonData)
    
    if (data.favorites) safeJSONStore(STORAGE_KEYS.FAVORITES, data.favorites)
    if (data.collections) safeJSONStore(STORAGE_KEYS.COLLECTIONS, data.collections)
    if (data.watched) safeJSONStore(STORAGE_KEYS.WATCHED, data.watched)
    if (data.watchlist) safeJSONStore(STORAGE_KEYS.WATCHLIST, data.watchlist)
    if (data.ratings) safeJSONStore(STORAGE_KEYS.USER_RATINGS, data.ratings)
    if (data.preferences) safeJSONStore(STORAGE_KEYS.PREFERENCES, data.preferences)
    if (data.searchHistory) safeJSONStore(SEARCH_HISTORY_KEY, data.searchHistory)
    
    return true
  } catch (error) {
    console.error('Error importing user data:', error)
    return false
  }
}

// ============ CLEAR ALL DATA ============

export const clearAllUserData = () => {
  Object.values(STORAGE_KEYS).forEach(key => {
    localStorage.removeItem(key)
  })
  localStorage.removeItem(SEARCH_HISTORY_KEY)
  return true
}

// ============ RECENT ACTIVITY ============

export const getRecentActivity = (limit = 10) => {
  const activities = []
  
  // Recent favorites
  const favorites = getFavorites()
  favorites.slice(-5).forEach(movie => {
    activities.push({
      type: 'favorite',
      movie: movie,
      date: movie.dateAdded,
      action: 'Added to favorites'
    })
  })
  
  // Recent watchlist
  const watchlist = getWatchlist()
  watchlist.slice(-5).forEach(movie => {
    activities.push({
      type: 'watchlist',
      movie: movie,
      date: movie.dateAdded,
      action: 'Added to watchlist'
    })
  })
  
  // Recent watched
  const watched = getWatchedMovies()
  watched.slice(-5).forEach(movie => {
    activities.push({
      type: 'watched',
      movie: movie,
      date: movie.watchedDate,
      action: 'Marked as watched'
    })
  })
  
  // Sort by date and limit
  return activities
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, limit)
}